import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';
import { MdLocationOn, MdDirectionsTransit } from 'react-icons/md';

export interface PoiCardMeta { label: string; value: string }

export interface PoiCardProps {
  title: string;
  address?: string;
  description?: string;
  metaData?: PoiCardMeta[];
  tags?: string[];
  imageUrl?: string;
  rawData?: any;
  onPress?: () => void;
  // optional callback when expand toggles
  onToggle?: (expanded: boolean) => void;
  transportNote?: string;
}

const DEFAULT_PLACEHOLDER = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop';

const PoiCard: React.FC<PoiCardProps> = ({
  title,
  address,
  description,
  metaData = [],
  tags = [],
  imageUrl,
  rawData, // not used by card UI but accepted for debugging callers
  onPress,
  transportNote,
}) => {
  const formatLocation = (addr: any) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    // addr is an object (jsonb). Prefer city/district/province, fallback to formatted_address
    try {
      const city = addr.city || addr.city_name || '';
      const district = addr.district || '';
      const province = addr.province || '';
      const formatted = addr.formatted_address || '';
      // prefer "city district", but fall back to formatted or province
      const parts = [] as string[];
      if (city) parts.push(city);
      if (district) parts.push(district);
      if (parts.length) return parts.join(' ');
      if (formatted) return formatted;
      if (province) return province;
      return '';
    } catch (e) {
      return '';
    }
  };

  const subtitle = formatLocation(address);

  // Card no longer expands inline. It should act as a tappable item that opens the full-screen modal.
  return (
    <TouchableOpacity style={styles.card} activeOpacity={0.95} onPress={onPress}>
      <View style={styles.imageWrapper}>
        <Image source={{ uri: imageUrl || DEFAULT_PLACEHOLDER }} style={styles.image} resizeMode="cover" />
      </View>

      <View style={styles.body}>
        <View style={styles.headerRow}>
          <Text style={styles.title} numberOfLines={2}>{title}</Text>

          {/* Right arrow indicates open full-screen */}
          <View style={styles.expandButtonHeader}>
            <PlatformIcon name="chevron_right" size={24} color="#9CA3AF" />
          </View>
        </View>

        {subtitle ? (
          <View style={styles.addressRow}>
            {React.createElement(MdLocationOn as any, { size: 16, color: '#9CA3AF' })}
            <Text style={styles.addressText} numberOfLines={1}>{subtitle}</Text>
          </View>
        ) : null}
        {transportNote ? (
          <View style={styles.transportRow}>
            {React.createElement(MdDirectionsTransit as any, { size: 16, color: '#9CA3AF' })}
            <Text style={styles.transportText} numberOfLines={2}>{transportNote}</Text>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    borderColor: '#E5E7EB',
    borderWidth: 1,
  // remove horizontal inset so the card reaches further to the edges (align with divider)
  marginHorizontal: 0,
  // add small top margin so the card is not too close to the divider above (reduced for grid compactness)
  marginTop: 8,
  },
  imageWrapper: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#F3F4F6', overflow: 'hidden', borderTopLeftRadius: 12, borderTopRightRadius: 12 },
  image: { width: '100%', height: '100%' },
  // reduce internal padding to match list/divider alignment above
  body: { padding: 16, position: 'relative' },
  headerRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { fontSize: 16, fontWeight: '600', color: '#111827', marginBottom: 6, flex: 1, lineHeight: 22 },
  addressRow: { flexDirection: 'row', alignItems: 'center', marginTop: 6 },
  addressText: { marginLeft: 8, color: '#6B7280', fontSize: 13, flex: 1, lineHeight: 18 },
  transportRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 6 },
  transportText: { marginLeft: 8, color: '#6B7280', fontSize: 13, flex: 1, lineHeight: 18 },
  expandButtonHeader: {
    marginLeft: 12,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expandedArea: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  sectionHeaderIcon: { color: '#0EA5FF' },
  sectionHeaderText: { marginLeft: 8, fontSize: 14, fontWeight: '600', color: '#111827' },
  infoCardPlain: { backgroundColor: '#FFFFFF' },
  infoBody: { color: '#6B7280', fontSize: 14, lineHeight: 20, paddingLeft: 32 },
  sectionDivider: { height: 1, backgroundColor: '#E5E7EB', marginVertical: 12 },
  metaBlock: { paddingVertical: 6 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  metaRowDivider: { borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  metaLabel: { fontSize: 11, color: '#374151', fontWeight: '600', marginRight: 6 },
  metaValue: { fontSize: 11, color: '#374151' },
  tagsRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, gap: 8 },
  tagPill: { backgroundColor: '#EEF2FF', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, marginRight: 6, marginBottom: 6 },
  tagText: { color: '#4338CA', fontSize: 11, fontWeight: '500' },
});

export default PoiCard;
